import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    ReferenceLine,
} from "recharts";

import { Select, theme } from "antd";

const calculateAnalytics = (data) => {
    if (!data?.length) return null;

    const sorted = [...data].sort((a, b) => a - b);

    const total = data.length;

    const median =
        sorted.length % 2 === 0
            ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
            : sorted[Math.floor(sorted.length / 2)];

    const p90Index = Math.ceil(total * 0.9) - 1;
    const p90 = sorted[p90Index];

    const avg = data.reduce((sum, val) => sum + val, 0) / total;

    const between0to2 = data.filter((v) => v >= 0 && v < 2).length;

    const between2to5 = data.filter((v) => v >= 2 && v < 5).length;

    const between5to8 = data.filter((v) => v >= 5 && v < 8).length;

    const over8 = data.filter((v) => v >= 8).length;

    return {
        avg: avg.toFixed(2),
        median: median.toFixed(2),
        p90: p90.toFixed(2),

        percent0to2: ((between0to2 / total) * 100).toFixed(2),

        percent2to5: ((between2to5 / total) * 100).toFixed(2),

        percent5to8: ((between5to8 / total) * 100).toFixed(2),

        percentOver8: ((over8 / total) * 100).toFixed(2),
    };
};

const createBellCurveData = (data, bucketSize = 1) => {
    if (!data?.length) return [];

    const mean = data.reduce((sum, val) => sum + val, 0) / data.length;

    const variance =
        data.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        data.length;

    const stdDev = Math.sqrt(variance);

    const max = Math.ceil(Math.max(...data));
    const safeStdDev = stdDev || 1;

    const chartData = [];

    for (let i = 0; i <= max; i += bucketSize) {
        const count = data.filter((v) => v >= i && v < i + bucketSize).length;

        const x = i + bucketSize / 2;

        const bellCurve =
            (1 / (safeStdDev * Math.sqrt(2 * Math.PI))) *
            Math.exp(-Math.pow(x - mean, 2) / (2 * Math.pow(safeStdDev, 2)));

        chartData.push({
            range: `${i}-${i + bucketSize}`,
            count,
            density: bellCurve * data.length * 0.9,
            start: i,
        });
    }

    return chartData;
};

const getBarColor = (value, token) => {
    if (value < 2) return token.colorError;

    if (value < 5) return token.colorWarning;

    if (value < 8) return token.colorPrimary;

    return token.colorSuccess;
};

const StatCard = ({ accent, title, value }) => {
    return (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
                <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: accent }}
                />
                <p className="m-0 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
                    {title}
                </p>
            </div>

            <div className="text-2xl font-semibold leading-none text-[var(--color-text)]">
                {value}
            </div>
        </div>
    );
};

const RangeLegend = ({ items }) => {
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {items.map((item) => (
                <div key={item.title} className="flex items-center gap-2">
                    <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: item.accent }}
                    />
                    <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                        {item.title}
                    </span>
                </div>
            ))}
        </div>
    );
};

const Header = ({ title, subtitle }) => {
    return (
        <div
            className="flex items-center justify-between"
            style={{ flexGrow: 1 }}
        >
            <div>
                <h3 className="section-title font-semibold text-foreground mb-1">
                    {title}
                </h3>
                <p className="section-subtitle text-gray-500">{subtitle}</p>
            </div>
        </div>
    );
};

const EmptyState = () => {
    return (
        <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-5 py-10 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary-light)]">
                <span className="h-3 w-3 rounded-full bg-[var(--color-primary)]" />
            </div>
            <h4 className="m-0 text-base font-semibold text-[var(--color-text)]">
                No transaction duration data
            </h4>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[var(--color-text-secondary)]">
                There are no completed transaction time records for the selected
                date range or hotel filters.
            </p>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
        <div
            style={{
                background: "var(--color-bg-elevated)",
                border: "1px solid var(--color-border)",
                padding: 14,
                borderRadius: 12,
                boxShadow: "var(--shadow-card)",
                minWidth: 140,
            }}
        >
            <div
                style={{
                    color: "var(--color-text)",
                    marginBottom: 6,
                    fontWeight: 600,
                }}
            >
                Range: {label}
            </div>

            <div
                style={{
                    color: "var(--color-text-secondary)",
                    fontSize: 13,
                }}
            >
                Transactions:
                <span
                    style={{
                        marginLeft: 6,
                        color: "var(--color-text)",
                        fontWeight: 600,
                    }}
                >
                    {payload[0]?.payload?.count || 0}
                </span>
            </div>
        </div>
    );
};

const TransactionAnalytics = ({
    transactionTimes = [],
    transactionChartFilterOptions = [],
    setTransactionChartFilter,
    transactionChartFilter = "all",
    transactionAnalyticsCounts = 0,
}) => {
    const { token } = theme.useToken();

    const handleFilterChange = (event) => {
        setTransactionChartFilter(event);
    };

    const analytics = useMemo(
        () => calculateAnalytics(transactionTimes),
        [transactionTimes],
    );

    const chartData = useMemo(
        () => createBellCurveData(transactionTimes),
        [transactionTimes],
    );

    const primaryStats = [
        {
            title: "P90 Transaction Time",
            value: `${analytics?.p90} min`,
            accent: token.colorError,
        },
        {
            title: "Median Transaction Time",
            value: `${analytics?.median} min`,
            accent: token.colorWarning,
        },
        {
            title: "Average Transaction Time",
            value: `${analytics?.avg} min`,
            accent: token.colorPrimary,
        },
    ];

    const rangeStats = [
        {
            title: "0-2 min",
            value: `${analytics?.percent0to2}%`,
            accent: token.colorError,
        },
        {
            title: "2-5 min",
            value: `${analytics?.percent2to5}%`,
            accent: token.colorWarning,
        },
        {
            title: "5-8 min",
            value: `${analytics?.percent5to8}%`,
            accent: token.colorPrimary,
        },
        {
            title: "Over 8 min",
            value: `${analytics?.percentOver8}%`,
            accent: token.colorSuccess,
        },
    ];

    return (
        <div className="chart-container animate-fade-in">
            <div
                className="mb-4 flex justify-between items-center"
                width="100%"
            >
                <Header
                    title="Transaction Duration Analytics"
                    subtitle="Insights into kiosk transaction durations"
                />

                <div className="flex flex-col" style={{ flexGrow: 1 }}>
                    <Select
                        placeholder="Search and select hotels"
                        defaultValue={
                            transactionChartFilterOptions[0]?.key || ""
                        }
                        onChange={handleFilterChange}
                    >
                        {transactionChartFilterOptions.map((option) => (
                            <Select.Option key={option.key} value={option.key}>
                                {option.title}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
            {!analytics ? (
                <EmptyState />
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        {primaryStats.map((stat) => (
                            <StatCard key={stat.title} {...stat} />
                        ))}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
                        {rangeStats.map((stat) => (
                            <StatCard key={stat.title} {...stat} />
                        ))}
                    </div>

                    <div className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
                        <div className="mb-5 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                                <h3 className="section-title font-semibold text-foreground mb-1">
                                    Transaction Time Distribution
                                </h3>
                                <p className="section-subtitle text-gray-500">
                                    Distribution of kiosk transaction durations
                                </p>
                            </div>
                            <RangeLegend items={rangeStats} />
                        </div>

                        <div
                            className="rounded-lg"
                            style={{
                                width: "100%",
                                height: 450,
                                background:
                                    "linear-gradient(180deg, var(--color-primary-light), transparent)",
                                padding: 10,
                            }}
                        >
                            <ResponsiveContainer>
                                <ComposedChart data={chartData}>
                                    <defs>
                                        <filter id="glow">
                                            <feGaussianBlur
                                                stdDeviation="4"
                                                result="coloredBlur"
                                            />

                                            <feMerge>
                                                <feMergeNode in="coloredBlur" />

                                                <feMergeNode in="SourceGraphic" />
                                            </feMerge>
                                        </filter>
                                    </defs>

                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke={token.colorBorderSecondary}
                                        vertical={false}
                                    />

                                    <XAxis
                                        dataKey="range"
                                        tick={{
                                            fill: token.colorTextSecondary,
                                            fontSize: 12,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                    />

                                    <YAxis
                                        tick={{
                                            fill: token.colorTextSecondary,
                                            fontSize: 12,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                    />

                                    <Tooltip content={<CustomTooltip />} />

                                    <Bar
                                        dataKey="count"
                                        radius={[8, 8, 0, 0]}
                                        barSize={32}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={index}
                                                fill={getBarColor(
                                                    entry.start,
                                                    token,
                                                )}
                                                fillOpacity={0.85}
                                            />
                                        ))}
                                    </Bar>

                                    <ReferenceLine
                                        x={`${Math.floor(analytics.median)}-${
                                            Math.floor(analytics.median) + 1
                                        }`}
                                        stroke={token.background}
                                        strokeDasharray="6 6"
                                        label={{
                                            value: "Median",
                                            position: "insideTop",
                                            offset: 100,
                                            fill: token.colorBgSolid,
                                            fontSize: 14,
                                            fontWeight: 600,
                                        }}
                                    />

                                    <ReferenceLine
                                        x={`${Math.floor(analytics.p90)}-${
                                            Math.floor(analytics.p90) + 1
                                        }`}
                                        stroke={token.background}
                                        strokeDasharray="6 6"
                                        label={{
                                            value: "P90",
                                            position: "insideBottom",
                                            offset: 4,
                                            fill: token.colorBgSolid,
                                            fontSize: 14,
                                            fontWeight: 600,
                                        }}
                                    />

                                    <Line
                                        type="natural"
                                        dataKey="density"
                                        stroke={token.colorPrimary}
                                        strokeWidth={2}
                                        strokeOpacity={0.95}
                                        dot={false}
                                        activeDot={false}
                                        filter="url(#glow)"
                                    />
                                </ComposedChart>
                                <h1 className="flex justify-center items-center font-semibold text-[var(--color-text)] text-lg">
                                    {transactionChartFilter === "all"
                                        ? `Total Check Ins & Walk Ins : ${transactionAnalyticsCounts}`
                                        : `Total ${
                                              transactionChartFilter === "1"
                                                  ? "Check Ins"
                                                  : "Walk Ins"
                                          }  : ${transactionAnalyticsCounts}`}
                                </h1>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default TransactionAnalytics;
